/* SMD product section — shelters + backpacks.
   Owns option selection, availability, gallery, add-ons and Add to bag.
   Cart: POSTs /cart/add.js then hands off to Rebuy Smart Cart. Never
   navigates to /cart (it redirects to the homepage on this store). */
(() => {
  if (customElements.get('smd-product')) return;

  const esc = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Mirrors Liquid's money_without_trailing_zeros for the store's money format. */
  function formatMoney(cents, format) {
    // Store format is wrapped in <span class=money>; prices are written as text.
    const fmt = (format || '${{amount}}').replace(/<[^>]*>/g, '');
    const key = (fmt.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    const fix = (precision, thousands = ',', decimal = '.') => {
      const [int, dec] = (cents / 100).toFixed(precision).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? grouped + decimal + dec : grouped;
    };
    const value = {
      amount: () => fix(2),
      amount_no_decimals: () => fix(0),
      amount_with_comma_separator: () => fix(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fix(0, '.', ','),
      amount_with_apostrophe_separator: () => fix(2, "'", '.'),
    }[key] || (() => fix(2));
    let out = fmt.replace(/\{\{\s*\w+\s*\}\}/, value());
    if (cents % 100 === 0) out = out.replace(/([.,])00(?!\d)/, '');
    return out;
  }

  class SmdProduct extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const json = this.querySelector('[data-smd-json]');
      if (!json) return;
      this.dataset.ready = 'true';

      this.data = JSON.parse(json.textContent);
      this.opts = this.data.options || [];
      this.variants = this.data.variants || [];
      this.form = this.querySelector('[data-smd-form]');
      this.variantInput = this.querySelector('[data-smd-variant-input]');

      const startId = Number(this.variantInput && this.variantInput.value);
      this.variant = this.variants.find((v) => v.id === startId) || this.variants.find((v) => v.a) || this.variants[0] || null;
      this.state = this.variant ? this.variant.o.slice() : [];
      this.badgeIndex = this.resolveBadgeIndex();

      this.querySelectorAll('select[data-smd-opt] option').forEach((o) => { o.dataset.base = o.textContent.trim(); });

      this.addEventListener('change', (e) => this.onChange(e));
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.setupGallery();
      this.setupDialogs();
      this.setupFit();
      this.render({ initial: true });
    }

    /* ---------- Option model ---------- */

    entry(i, value) {
      const opt = this.opts[i];
      return (opt && opt.values.find((x) => x.v === value)) || { v: value, g: '', t: '' };
    }

    resolveBadgeIndex() {
      if (!this.opts.length || (this.opts.length === 1 && this.opts[0].name === 'Title')) return -1;
      const wanted = String(this.data.badgeOption || '').split(',').map((s) => s.trim()).filter(Boolean);
      if (!wanted.length) return 0;
      for (const name of wanted) {
        const idx = this.opts.findIndex((o) => o.name === name);
        if (idx > -1) return idx;
      }
      return -1;
    }

    matchesOthers(variant, i) {
      return variant.o.every((x, k) => k === i || x === this.state[k]);
    }

    /* 'ok' | 'soldout' | 'none' for a set of candidate values of option i,
       holding every other option at its current selection. */
    status(i, candidates) {
      let exists = false;
      for (const v of this.variants) {
        if (!candidates.includes(v.o[i]) || !this.matchesOthers(v, i)) continue;
        exists = true;
        if (v.a) return 'ok';
      }
      return exists ? 'soldout' : 'none';
    }

    candidatesFor(i, part, value) {
      const opt = this.opts[i];
      if (!opt) return [];
      const cur = this.entry(i, this.state[i]);
      if (part === 'group') return opt.values.filter((x) => x.g === value).map((x) => x.v);
      if (part === 'size') {
        return opt.values.filter((x) => x.t === value && (!opt.carded || x.g === cur.g)).map((x) => x.v);
      }
      return [value];
    }

    onChange(e) {
      const el = e.target;
      if (el.matches('[data-smd-kit-item]')) {
        el.dataset.touched = 'true';
        this.renderKit(el.closest('[data-smd-kit]'));
        return;
      }
      if (el.matches('[data-smd-kitx-variant]')) {
        const item = el.closest('[data-smd-kitx-item]');
        const opt = el.selectedOptions[0];
        const price = item && item.querySelector('[data-smd-kitx-price]');
        if (price && opt) price.textContent = opt.dataset.price;
        const btn = item && item.querySelector('[data-smd-kitx-add]');
        if (btn) { btn.dataset.variantId = el.value; btn.removeAttribute('data-state'); btn.setAttribute('aria-label', btn.dataset.labelAdd); }
        return;
      }
      if (el.matches('[data-smd-addon]')) {
        this.renderAddon();
        this.renderTotals();
        return;
      }
      if (!el.matches('[data-smd-opt]')) return;

      const i = Number(el.dataset.smdOpt);
      const part = el.dataset.smdPart;
      const opt = this.opts[i];
      if (!opt) return;
      const cur = this.entry(i, this.state[i]);
      const live = (x) => this.status(i, [x.v]) === 'ok';

      let next;
      if (part === 'group') {
        const inGroup = opt.values.filter((x) => x.g === el.value);
        next = inGroup.find((x) => x.t === cur.t && live(x)) || inGroup.find(live) || inGroup.find((x) => x.t === cur.t) || inGroup[0];
      } else if (part === 'size') {
        const pool = opt.values.filter((x) => x.t === el.value);
        next = (opt.carded && pool.find((x) => x.g === cur.g)) || pool[0];
      } else {
        next = opt.values.find((x) => x.v === el.value);
      }
      if (!next) return;

      this.state[i] = next.v;
      this.variant = this.resolveVariant(i);
      if (this.variant) this.state = this.variant.o.slice();
      this.render({ changed: i });
    }

    resolveVariant(changed) {
      const exact = this.variants.find((v) => v.o.every((x, k) => x === this.state[k]));
      if (exact) return exact;
      // Combination doesn't exist: keep what was just chosen, stay as close as
      // possible to everything else, prefer something in stock.
      let best = null;
      let bestScore = -1;
      for (const v of this.variants) {
        if (v.o[changed] !== this.state[changed]) continue;
        let score = v.a ? 1 : 0;
        v.o.forEach((x, k) => { if (x === this.state[k]) score += 2; });
        if (score > bestScore) { best = v; bestScore = score; }
      }
      return best;
    }

    /* ---------- Rendering ---------- */

    render({ initial = false } = {}) {
      this.renderControls();
      this.renderLabels();
      this.renderAddon();
      this.renderTotals();
      this.renderButton();

      if (!initial) {
        if (this.variant && this.variant.m) this.goTo(this.variant.m);
        if (this.dataset.updateUrl === 'true' && this.variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', this.variant.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
        this.dispatchEvent(new CustomEvent('smd:variant-change', { bubbles: true, detail: { variant: this.variant } }));
      }
    }

    renderControls() {
      this.querySelectorAll('[data-smd-opt]').forEach((el) => {
        const i = Number(el.dataset.smdOpt);
        const part = el.dataset.smdPart;
        const cur = this.entry(i, this.state[i]);

        if (el.type === 'radio') {
          el.checked = el.value === (part === 'group' ? cur.g : cur.v);
          const st = this.status(i, this.candidatesFor(i, part, el.value));
          el.toggleAttribute('data-soldout', st === 'soldout');
          const card = el.nextElementSibling;
          const note = card && card.querySelector('[data-smd-status]');
          if (note) note.textContent = st === 'soldout' ? 'Sold out' : '';
          return;
        }

        if (el.tagName === 'SELECT') {
          Array.from(el.options).forEach((o) => {
            const st = this.status(i, this.candidatesFor(i, part, o.value));
            o.hidden = st === 'none';
            o.disabled = st === 'none';
            o.textContent = o.dataset.base + (st === 'soldout' ? ' (sold out)' : '');
          });
          el.value = part === 'size' ? cur.t : cur.v;
        }
      });
    }

    renderLabels() {
      this.querySelectorAll('[data-smd-value-label]').forEach((node) => {
        node.textContent = this.state[Number(node.dataset.smdValueLabel)] || '';
      });

      const badge = this.querySelector('[data-smd-badge]');
      if (badge) badge.textContent = this.badgeIndex > -1 ? this.state[this.badgeIndex] || '' : '';

      // The first card group drives [style] tokens, the close-up link and its helper note.
      const card = this.querySelector('input[data-smd-part="group"]:checked');
      const label = card ? card.dataset.label : '';
      this.querySelectorAll('[data-smd-style-label]').forEach((n) => { n.textContent = label; });

      const closeup = this.querySelector('[data-smd-closeup]');
      if (closeup) closeup.hidden = !(card && card.dataset.closeup);

      this.querySelectorAll('[data-smd-helper]').forEach((note) => {
        const checked = this.querySelector(`input[data-smd-part="group"][data-smd-opt="${note.dataset.smdHelper}"]:checked`);
        const text = checked ? checked.dataset.helper : '';
        note.innerHTML = text ? `<strong>${esc(checked.dataset.label)}:</strong> ${esc(text)}` : '';
        note.hidden = !text;
      });
    }

    selectedAddon() {
      return this.querySelector('[data-smd-addon]:checked');
    }

    renderAddon() {
      const note = this.querySelector('[data-smd-addon-helper]');
      const addon = this.selectedAddon();
      if (!note) return;
      const text = addon ? addon.dataset.helper : '';
      note.innerHTML = text ? `<strong>${esc(addon.dataset.label)}:</strong> ${esc(text)}` : '';
      note.hidden = !text;
    }

    summaryText() {
      const parts = [];
      this.querySelectorAll('input[data-smd-part="group"]:checked, select[data-smd-opt]').forEach((el) => {
        if (el.tagName === 'INPUT') {
          parts.push(el.dataset.label);
        } else {
          const cur = this.entry(Number(el.dataset.smdOpt), this.state[Number(el.dataset.smdOpt)]);
          const token = el.dataset.smdPart === 'size' ? cur.t : cur.v;
          if (token) parts.push(el.dataset.summarySuffix ? `${token} ${el.dataset.summarySuffix}` : token);
        }
      });
      let text = parts.filter(Boolean).join(' · ') || (this.form ? this.form.dataset.summaryBase : '');
      const addon = this.selectedAddon();
      if (addon && addon.dataset.summary) text = text ? `${text} + ${addon.dataset.summary}` : addon.dataset.summary;
      return text;
    }

    renderTotals() {
      const v = this.variant;
      const fmt = this.data.moneyFormat;
      const price = this.querySelector('[data-smd-price]');
      const compare = this.querySelector('[data-smd-compare]');
      if (v && price) price.textContent = formatMoney(v.p, fmt);
      if (compare) {
        const onSale = v && v.c > v.p;
        compare.hidden = !onSale;
        if (onSale) compare.textContent = formatMoney(v.c, fmt);
      }

      const addon = this.selectedAddon();
      const addonPrice = addon && addon.dataset.variantId ? Number(addon.dataset.price) || 0 : 0;
      const total = this.querySelector('[data-smd-total]');
      if (total && v) total.textContent = formatMoney(v.p + addonPrice, fmt);

      const summary = this.querySelector('[data-smd-summary]');
      if (summary) summary.textContent = this.summaryText();
    }

    renderButton() {
      if (!this.form) return;
      const btn = this.form.querySelector('[data-smd-atc]');
      const label = this.form.querySelector('[data-smd-atc-label]');
      const v = this.variant;
      const d = this.form.dataset;
      if (this.variantInput && v) this.variantInput.value = v.id;
      if (!btn || !label) return;
      if (!v) { btn.disabled = true; label.textContent = d.labelUnavailable; }
      else if (!v.a) { btn.disabled = true; label.textContent = d.labelSoldout; }
      else { btn.disabled = false; label.textContent = d.labelAdd; }
    }

    /* ---------- Add to bag ---------- */

    async onSubmit(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!this.variant || !this.variant.a || this.busy) return;

      const btn = this.form.querySelector('[data-smd-atc]');
      const label = this.form.querySelector('[data-smd-atc-label]');
      const error = this.form.querySelector('[data-smd-error]');
      const d = this.form.dataset;

      // Carry any properties[...] inputs from custom/app blocks.
      const properties = {};
      for (const [key, value] of new FormData(this.form).entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value) properties[m[1]] = value;
      }

      const items = [{ id: this.variant.id, quantity: 1, properties }];
      const addon = this.selectedAddon();
      if (addon) {
        const propName = (this.querySelector('[data-smd-addon-property-name]') || {}).value;
        if (propName && addon.dataset.property) properties[propName] = addon.dataset.property;
        const addonId = Number(addon.dataset.variantId);
        if (addonId) items.push({ id: addonId, quantity: 1 });
      }

      this.busy = true;
      if (error) error.hidden = true;
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      label.textContent = d.labelAdding;

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.description || body.message || 'This item could not be added. Try again.');

        label.textContent = d.labelAdded;
        this.notifyCart();
        this.dispatchEvent(new CustomEvent('smd:added', { bubbles: true, detail: { items } }));
        setTimeout(() => this.renderButton(), 1800);
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
        this.renderButton();
      } finally {
        this.busy = false;
        btn.removeAttribute('aria-busy');
        if (label.textContent !== d.labelAdded) this.renderButton();
      }
    }

    // Same hand-off the live seam sealing selector uses. Do NOT add a
    // navigation fallback: /cart redirects to the homepage on this store.
    // open=false refreshes the cart (count, drawer contents) without opening it.
    notifyCart(open = true) {
      try {
        const smartCart = window.Rebuy && window.Rebuy.SmartCart;
        if (smartCart) {
          if (typeof smartCart.refresh === 'function') smartCart.refresh();
          if (open && typeof smartCart.show === 'function') smartCart.show();
          else if (open && typeof smartCart.open === 'function') smartCart.open();
        }
      } catch (e) { /* fall through to events */ }

      const events = open
        ? ['rebuy:cart.refresh', 'rebuy:smartcart.show', 'cart:refresh', 'cart:added']
        : ['rebuy:cart.refresh', 'cart:refresh'];
      events.forEach((name) => {
        try {
          document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true }));
          document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
        } catch (e) { /* noop */ }
      });

      // Impact's cart:change listeners read event.detail.cart (header count).
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        })
        .catch(() => { /* count refreshes on next page view */ });
    }

    /* ---------- Gallery ---------- */

    setupGallery() {
      this.stage = this.querySelector('[data-smd-stage]');
      if (!this.stage) return;
      this.slides = Array.from(this.stage.querySelectorAll('[data-smd-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-smd-thumb]'));

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.mediaId))));

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            // While goTo() scrolls to a chosen slide, ignore the slides the stage
            // passes on the way, so the thumbnail strip doesn't chase them.
            if (this.pendingSlide && en.target !== this.pendingSlide) return;
            this.pendingSlide = null;
            this.setActive(en.target);
          });
        }, { root: this.stage, threshold: 0.6 });
        this.slides.forEach((s) => io.observe(s));
      }

      ['pointerdown', 'wheel', 'touchstart'].forEach((type) => {
        this.stage.addEventListener(type, () => { this.pendingSlide = null; }, { passive: true });
      });

      this.stage.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const idx = this.slides.indexOf(this.activeSlide) + (e.key === 'ArrowRight' ? 1 : -1);
        const target = this.slides[Math.max(0, Math.min(this.slides.length - 1, idx))];
        if (target) this.goTo(Number(target.dataset.mediaId));
      });

      const zoom = this.querySelector('[data-smd-zoom]');
      if (zoom) zoom.addEventListener('click', () => this.openGalleryZoom(this.activeSlide || this.slides[0]));
      this.setupZoomNav();

      // Keep the active slide aligned if the stage width changes (scrollbar
      // appearing, late-loading content, rotation).
      if ('ResizeObserver' in window) {
        let lastWidth = this.stage.clientWidth;
        new ResizeObserver(() => {
          const w = this.stage.clientWidth;
          if (w === lastWidth || !this.activeSlide) return;
          lastWidth = w;
          this.stage.scrollTo({ left: this.slides.indexOf(this.activeSlide) * w, behavior: 'auto' });
        }).observe(this.stage);
      }

      this.setActive(this.slides[0]);
      const initial = this.stage.querySelector('[data-initial]');
      if (initial && initial !== this.slides[0]) this.goTo(Number(initial.dataset.mediaId), false);
    }

    goTo(mediaId, smooth = true) {
      if (!this.stage) return;
      const idx = this.slides.findIndex((s) => Number(s.dataset.mediaId) === mediaId);
      if (idx < 0) return;
      const animate = smooth && !reducedMotion();
      this.pendingSlide = animate ? this.slides[idx] : null;
      clearTimeout(this.pendingTimer);
      if (animate) this.pendingTimer = setTimeout(() => { this.pendingSlide = null; }, 1200);
      this.stage.scrollTo({ left: idx * this.stage.clientWidth, behavior: animate ? 'smooth' : 'auto' });
      this.setActive(this.slides[idx]);
    }

    setActive(slide) {
      if (!slide || slide === this.activeSlide) return;
      if (this.activeSlide) this.activeSlide.querySelectorAll('video').forEach((v) => v.pause());
      this.activeSlide = slide;
      const id = slide.dataset.mediaId;
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.mediaId === id)));
      const thumb = this.thumbs.find((t) => t.dataset.mediaId === id);
      if (thumb && thumb.parentElement) {
        // Measure against the strip itself: offsetLeft is relative to the nearest
        // positioned ancestor, which overshot the strip to the right on desktop.
        const strip = thumb.parentElement;
        if (strip.scrollWidth > strip.clientWidth + 1) {
          const sr = strip.getBoundingClientRect();
          const tr = thumb.getBoundingClientRect();
          const max = strip.scrollWidth - strip.clientWidth;
          const left = strip.scrollLeft + (tr.left - sr.left) - (strip.clientWidth - tr.width) / 2;
          strip.scrollTo({ left: Math.max(0, Math.min(max, left)), behavior: 'auto' });
        }
      }
    }

    /* ---------- Dialogs ---------- */

    setupDialogs() {
      this.zoomDialog = this.querySelector('[data-smd-zoom-dialog]');

      this.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-smd-dialog-open]');
        if (opener) {
          const dialog = document.getElementById(opener.dataset.smdDialogOpen);
          if (dialog && dialog.hasAttribute('data-smd-kit')) this.syncKit(dialog);
          if (dialog && dialog.hasAttribute('data-smd-fit')) this.syncFit(dialog);
          if (dialog && dialog.showModal) dialog.showModal();
          return;
        }
        const closer = e.target.closest('[data-smd-dialog-close]');
        if (closer) { const dlg = closer.closest('dialog'); if (dlg) dlg.close(); return; }
        if (e.target.tagName === 'DIALOG') { e.target.close(); return; }
        const closeupBtn = e.target.closest('[data-smd-closeup]');
        if (closeupBtn) { this.openCloseup(closeupBtn); return; }
        const kitBtn = e.target.closest('[data-smd-kitx-add]');
        if (kitBtn) this.addKitItem(kitBtn);
      });
    }

    /* ---------- Complete your kit ---------- */

    // Adds one accessory without opening the cart, so shoppers can add
    // several and keep configuring the main product.
    async addKitItem(btn) {
      if (btn.dataset.state === 'busy') return;
      const id = Number(btn.dataset.variantId);
      if (!id) return;
      const status = this.querySelector('[data-smd-kitx-status]');
      btn.dataset.state = 'busy';
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.description || body.message || 'This item could not be added.');
        btn.dataset.state = 'added';
        btn.setAttribute('aria-label', btn.dataset.labelAdded);
        if (status) status.textContent = btn.dataset.labelAdded;
        this.notifyCart(false);
      } catch (err) {
        btn.removeAttribute('data-state');
        if (status) status.textContent = err.message;
      }
    }

    /* ---------- Harness close-up ---------- */

    openCloseup(btn) {
      const step = btn.closest('[data-smd-step]');
      const card = step && step.querySelector('input[data-smd-part="group"]:checked');
      if (!card || !card.dataset.closeup) return;
      const dialog = step.querySelector('[data-smd-closeup-dialog]');
      if (!dialog || !dialog.showModal) { this.openZoom(card.dataset.closeup, card.dataset.label); return; }
      const heading = (dialog.dataset.heading || '[style]').replace('[style]', card.dataset.label || '');
      dialog.querySelector('[data-smd-closeup-title]').textContent = heading;
      const img = dialog.querySelector('[data-smd-closeup-img]');
      img.src = card.dataset.closeup;
      img.alt = heading;
      const text = dialog.querySelector('[data-smd-closeup-text]');
      text.textContent = card.dataset.closeupText || '';
      text.hidden = !card.dataset.closeupText;
      dialog.showModal();
    }

    /* ---------- Fit finder ---------- */

    setupFit() {
      this.querySelectorAll('[data-smd-fit]').forEach((dialog) => {
        try { dialog.smdRules = JSON.parse(dialog.dataset.rules || '{}'); } catch (e) { dialog.smdRules = {}; }
        const form = dialog.querySelector('[data-smd-fit-form]');
        if (form) form.addEventListener('submit', (e) => { e.preventDefault(); e.stopPropagation(); this.runFit(dialog); });
        const apply = dialog.querySelector('[data-smd-fit-apply]');
        if (apply) apply.addEventListener('click', () => this.applyFit(dialog));
      });
    }

    fitOptionIndex(name) {
      return this.opts.findIndex((o) => o.name === name);
    }

    // Offer the harness styles from the cards on the page, preselecting the current one.
    syncFit(dialog) {
      const fields = (dialog.smdRules && dialog.smdRules.fields) || [];
      const wrap = dialog.querySelector('[data-smd-fit-style-wrap]');
      const select = dialog.querySelector('[data-smd-fit-style]');
      dialog.smdStyleOpt = -1;
      for (const f of fields) {
        const i = this.fitOptionIndex(f.option);
        if (i > -1 && this.opts[i].carded) { dialog.smdStyleOpt = i; break; }
      }
      if (!wrap || !select) return;
      if (dialog.smdStyleOpt < 0) { wrap.hidden = true; return; }
      const cards = Array.from(this.querySelectorAll(`input[data-smd-part="group"][data-smd-opt="${dialog.smdStyleOpt}"]`));
      select.innerHTML = cards.map((c) => `<option value="${esc(c.value)}"${c.checked ? ' selected' : ''}>${esc(c.dataset.label)}</option>`).join('');
      wrap.hidden = cards.length < 2;
    }

    runFit(dialog) {
      const d = dialog.dataset;
      const fields = (dialog.smdRules && dialog.smdRules.fields) || [];
      const select = dialog.querySelector('[data-smd-fit-style]');
      const styleLabel = select && select.selectedOptions[0] ? select.selectedOptions[0].textContent : '';
      const lines = [];
      const results = [];
      let needsHelp = false;
      let entered = 0;

      fields.forEach((f, idx) => {
        const input = dialog.querySelector(`[data-smd-fit-input="${idx}"]`);
        const raw = input ? input.value.trim() : '';
        if (!raw) return;
        entered += 1;
        const label = (f.label || '').replace(/\s*\(.*\)\s*$/, '').toLowerCase();
        const i = this.fitOptionIndex(f.option);
        const name = i > -1 && this.opts[i].carded && styleLabel ? `${styleLabel} ${f.result_label || ''}`.trim() : (f.result_label || f.option);
        const v = parseFloat(raw.replace(',', '.'));
        if (Number.isNaN(v)) { lines.push(`<li>${esc(d.msgInvalid.replace('[label]', label))}</li>`); return; }

        const sizes = f.sizes || [];
        let size = null;
        let issue = null;
        if (!sizes.length || v < sizes[0].min || v > sizes[sizes.length - 1].max) {
          issue = d.msgRange;
        } else {
          const hit = sizes.find((s) => v >= s.min && v <= s.max);
          if (hit) size = hit.size;
          else {
            const k = sizes.findIndex((s, n) => sizes[n + 1] && v > s.max && v < sizes[n + 1].min);
            if (f.between === 'smaller' && k > -1) size = sizes[k].size;
            else if (f.between === 'larger' && k > -1) size = sizes[k + 1].size;
            else issue = d.msgBetween;
          }
        }
        if (size) {
          results.push({ i, size });
          lines.push(`<li>${esc(name.charAt(0).toUpperCase() + name.slice(1))}: <strong>${esc(size)}</strong></li>`);
        } else {
          needsHelp = true;
          lines.push(`<li>${esc(issue.replace('[label]', label))}</li>`);
        }
      });

      const box = dialog.querySelector('[data-smd-fit-result]');
      const list = dialog.querySelector('[data-smd-fit-lines]');
      const note = dialog.querySelector('[data-smd-fit-note]');
      const apply = dialog.querySelector('[data-smd-fit-apply]');
      if (!entered) lines.push(`<li>${esc(d.msgEmpty)}</li>`);
      list.innerHTML = lines.join('');
      if (needsHelp) {
        const link = d.contactUrl ? ` <a href="${esc(d.contactUrl)}" target="_blank" rel="noopener">${esc(d.contactLabel)}</a>` : ` ${esc(d.contactLabel)}`;
        note.innerHTML = link.trim();
      } else {
        note.textContent = results.length ? d.msgOk : '';
      }
      note.hidden = !note.innerHTML;
      apply.hidden = !results.length;
      dialog.smdResults = results;
      box.hidden = false;
    }

    applyFit(dialog) {
      const results = dialog.smdResults || [];
      const select = dialog.querySelector('[data-smd-fit-style]');
      const styleOpt = dialog.smdStyleOpt;
      let changed = -1;

      // Harness style first, keeping the current size until a result replaces it.
      if (styleOpt > -1 && select && select.value) {
        const opt = this.opts[styleOpt];
        const cur = this.entry(styleOpt, this.state[styleOpt]);
        const pick = opt.values.find((x) => x.g === select.value && x.t === cur.t) || opt.values.find((x) => x.g === select.value);
        if (pick) { this.state[styleOpt] = pick.v; changed = styleOpt; }
      }
      results.forEach(({ i, size }) => {
        if (i < 0) return;
        const opt = this.opts[i];
        const cur = this.entry(i, this.state[i]);
        const pick = (opt.carded && opt.values.find((x) => x.g === cur.g && x.t === size))
          || opt.values.find((x) => x.t === size)
          || opt.values.find((x) => x.v === size);
        if (pick) { this.state[i] = pick.v; changed = i; }
      });
      if (changed < 0) { dialog.close(); return; }
      this.variant = this.resolveVariant(changed);
      if (this.variant) this.state = this.variant.o.slice();
      this.render({ changed });
      dialog.close();
    }

    /* ---------- Kit checklist ---------- */

    // The seam-sealing item follows the choice in the sealing step until the
    // shopper ticks or unticks it themselves.
    syncKit(dialog) {
      const addon = this.selectedAddon();
      const covered = !!(addon && addon.value !== 'none');
      dialog.querySelectorAll('[data-smd-kit-item][data-sealing]').forEach((box) => {
        if (!box.dataset.touched) box.checked = covered;
      });
      this.renderKit(dialog);
    }

    renderKit(dialog) {
      if (!dialog) return;
      const status = dialog.querySelector('[data-smd-kit-status]');
      if (!status) return;
      const todo = Array.from(dialog.querySelectorAll('[data-smd-kit-item]'))
        .filter((box) => !box.checked)
        .map((box) => box.dataset.short)
        .filter(Boolean);
      status.textContent = todo.length ? `${status.dataset.todo} ${todo.join(', ')}.` : status.dataset.done;
    }

    openZoom(src, alt) {
      if (!this.zoomDialog || !this.zoomDialog.showModal) { window.open(src, '_blank', 'noopener'); return; }
      // Single-image zoom (e.g. the harness close-up fallback): no gallery navigation.
      this.zoomIndex = null;
      this.zoomDialog.removeAttribute('data-smd-zoom-nav');
      const img = this.zoomDialog.querySelector('[data-smd-zoom-img]');
      img.src = src;
      img.alt = alt || '';
      this.zoomDialog.showModal();
    }

    /* ---------- Gallery zoom with previous / next ---------- */

    setupZoomNav() {
      const dlg = this.querySelector('[data-smd-zoom-dialog]');
      this.zoomSlides = (this.slides || []).filter((s) => s.dataset.zoomSrc);
      if (!dlg || this.zoomSlides.length < 2 || dlg.querySelector('[data-smd-zoom-step]')) return;
      const chevron = (d) => `<svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20"><path d="${d}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const make = (dir, label, path) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `smd-zoom-nav smd-zoom-nav--${dir}`;
        b.dataset.smdZoomStep = dir === 'prev' ? '-1' : '1';
        b.setAttribute('aria-label', label);
        b.innerHTML = chevron(path);
        b.addEventListener('click', (e) => { e.stopPropagation(); this.stepZoom(Number(b.dataset.smdZoomStep)); });
        return b;
      };
      const count = document.createElement('p');
      count.className = 'smd-zoom-count';
      count.setAttribute('aria-live', 'polite');
      count.dataset.smdZoomCount = '';
      dlg.append(make('prev', 'Previous image', 'M12.5 4 6.5 10l6 6'), make('next', 'Next image', 'm7.5 4 6 6-6 6'), count);
      dlg.addEventListener('keydown', (e) => {
        if (this.zoomIndex == null) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          this.stepZoom(e.key === 'ArrowRight' ? 1 : -1);
        }
      });
      // Closing leaves the gallery on the image the shopper ended on.
      dlg.addEventListener('close', () => {
        if (this.zoomIndex == null) return;
        const slide = this.zoomSlides[this.zoomIndex];
        if (slide) this.goTo(Number(slide.dataset.mediaId), false);
      });
    }

    openGalleryZoom(slide) {
      if (!slide || !slide.dataset.zoomSrc) return;
      const idx = (this.zoomSlides || []).indexOf(slide);
      if (!this.zoomDialog || !this.zoomDialog.showModal || idx < 0 || this.zoomSlides.length < 2) {
        const img = slide.querySelector('img');
        this.openZoom(slide.dataset.zoomSrc, img ? img.alt : '');
        return;
      }
      this.zoomDialog.setAttribute('data-smd-zoom-nav', '');
      this.showZoomAt(idx);
      if (!this.zoomDialog.open) this.zoomDialog.showModal();
    }

    stepZoom(delta) {
      if (this.zoomIndex == null || !this.zoomSlides.length) return;
      const n = this.zoomSlides.length;
      this.showZoomAt((this.zoomIndex + delta + n) % n);
    }

    showZoomAt(idx) {
      const slide = this.zoomSlides[idx];
      if (!slide) return;
      this.zoomIndex = idx;
      const img = this.zoomDialog.querySelector('[data-smd-zoom-img]');
      const source = slide.querySelector('img');
      img.src = slide.dataset.zoomSrc;
      img.alt = source ? source.alt : '';
      const count = this.zoomDialog.querySelector('[data-smd-zoom-count]');
      if (count) count.textContent = `${idx + 1} / ${this.zoomSlides.length}`;
    }
  }

  customElements.define('smd-product', SmdProduct);
})();
